import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import apiClient from '../utils/api';
import { useAuthStore } from '../store/authStore';
import { Button } from '../components/ui/button';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { 
  FileText, Save, Speaker, Lightbulb, Music2, Coffee, Car, Hotel, Users
} from 'lucide-react';
import { toast } from 'sonner';

export default function ArtistRider() {
  const navigate = useNavigate();
  const { user } = useAuthStore();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState('technical');
  
  const [rider, setRider] = useState({
    sound_requirements: '',
    stage_requirements: '',
    lighting_requirements: '',
    dj_equipment: '',
    backline: '',
    monitors: '',
    other_technical: '',
    dressing_room: '',
    catering: '',
    beverages: '',
    transportation: '',
    accommodation: '',
    guest_list: '',
    other_hospitality: ''
  });

  useEffect(() => {
    fetchRider();
  }, []);

  const fetchRider = async () => {
    try {
      setLoading(true);
      const response = await apiClient.get('/artist/rider');
      if (response.data) {
        const tech = response.data.technical || {};
        const hosp = response.data.hospitality || {};
        setRider({
          sound_requirements: tech.sound_requirements || '',
          stage_requirements: tech.stage_requirements || '',
          lighting_requirements: tech.lighting_requirements || '',
          dj_equipment: tech.dj_equipment || '',
          backline: tech.backline || '',
          monitors: tech.monitors || '',
          other_technical: tech.other_technical || '',
          dressing_room: hosp.dressing_room || '',
          catering: hosp.catering || '',
          beverages: hosp.beverages || '',
          transportation: hosp.transportation || '',
          accommodation: hosp.accommodation || '',
          guest_list: hosp.guest_list || '',
          other_hospitality: hosp.other_hospitality || ''
        });
      }
    } catch (error) {
      if (error.response?.status === 404) {
        toast.error('Artist profile not found');
        navigate('/artist/create');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await apiClient.put('/artist/rider', rider);
      toast.success('Rider saved successfully!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save rider');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="relative z-10 min-h-screen pt-24 pb-12 px-4 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-violet-500" />
      </div>
    );
  }

  const TextAreaField = ({ label, value, onChange, placeholder, icon: Icon }) => (
    <div className="space-y-2">
      <Label className="text-white/80 flex items-center gap-2">
        {Icon && <Icon className="w-4 h-4 text-violet-400" />}
        {label}
      </Label>
      <textarea
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        rows={3}
        className="w-full bg-white/5 border border-white/10 rounded-lg px-4 py-3 text-white placeholder:text-white/30 focus:border-violet-500 outline-none resize-none"
      />
    </div>
  );

  return (
    <div className="relative z-10 min-h-screen pt-24 pb-12 px-4">
      <div className="max-w-4xl mx-auto">
        <div className="flex items-center gap-3 mb-4">
          <FileText className="w-8 h-8 text-violet-400" />
          <h1 className="text-3xl font-bold text-white">Technical Rider</h1>
        </div>
        
        <p className="text-white/60 mb-8">
          Define your technical and hospitality requirements. Clients will see this when booking you.
        </p>

        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
          <TabsList className="bg-white/5 border border-white/10 p-1">
            <TabsTrigger value="technical" className="data-[state=active]:bg-violet-600">
              <Speaker className="w-4 h-4 mr-2" />
              Technical Requirements
            </TabsTrigger>
            <TabsTrigger value="hospitality" className="data-[state=active]:bg-violet-600">
              <Coffee className="w-4 h-4 mr-2" />
              Hospitality
            </TabsTrigger>
          </TabsList>

          {/* Technical Requirements Tab */}
          <TabsContent value="technical">
            <Card className="bg-white/5 border-white/10">
              <CardHeader>
                <CardTitle className="text-white flex items-center gap-2">
                  <Speaker className="w-5 h-5 text-violet-400" />
                  Technical Requirements
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-6">
                <TextAreaField
                  label="Sound System Requirements"
                  icon={Speaker}
                  value={rider.sound_requirements}
                  onChange={(e) => setRider({ ...rider, sound_requirements: e.target.value })}
                  placeholder="e.g. Minimum 10,000W PA system, full-range speakers, adequate subwoofers for bass-heavy music..."
                />

                <TextAreaField
                  label="DJ Equipment"
                  icon={Music2}
                  value={rider.dj_equipment}
                  onChange={(e) => setRider({ ...rider, dj_equipment: e.target.value })}
                  placeholder="e.g. 2x Pioneer CDJ-3000, 1x DJM-900NXS2 mixer, or I will bring my own controller..."
                />

                <TextAreaField
                  label="Monitors"
                  value={rider.monitors}
                  onChange={(e) => setRider({ ...rider, monitors: e.target.value })}
                  placeholder="e.g. 2x 15-inch monitor wedges with independent mix, positioned at 45-degree angles..."
                />

                <TextAreaField
                  label="Stage Requirements"
                  value={rider.stage_requirements}
                  onChange={(e) => setRider({ ...rider, stage_requirements: e.target.value })}
                  placeholder="e.g. Minimum 8x6 ft DJ booth, stable table for equipment, easy cable access..."
                />

                <TextAreaField
                  label="Lighting Requirements"
                  icon={Lightbulb}
                  value={rider.lighting_requirements}
                  onChange={(e) => setRider({ ...rider, lighting_requirements: e.target.value })}
                  placeholder="e.g. Moving head lights, strobe effects, smoke/haze machine, DMX controller..."
                />

                <TextAreaField
                  label="Backline (if applicable)"
                  value={rider.backline}
                  onChange={(e) => setRider({ ...rider, backline: e.target.value })}
                  placeholder="e.g. Drum kit, keyboard/synth stands, DI boxes for live instruments..."
                />

                <TextAreaField
                  label="Other Technical Requirements"
                  value={rider.other_technical}
                  onChange={(e) => setRider({ ...rider, other_technical: e.target.value })}
                  placeholder="Any other technical requirements or special requests..."
                />
              </CardContent>
            </Card>
          </TabsContent>

          {/* Hospitality Tab */}
          <TabsContent value="hospitality">
            <Card className="bg-white/5 border-white/10">
              <CardHeader>
                <CardTitle className="text-white flex items-center gap-2">
                  <Coffee className="w-5 h-5 text-violet-400" />
                  Hospitality Requirements
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-6">
                <TextAreaField
                  label="Dressing Room"
                  value={rider.dressing_room}
                  onChange={(e) => setRider({ ...rider, dressing_room: e.target.value })}
                  placeholder="e.g. Private, secure room with mirror, comfortable seating, electrical outlets..."
                />

                <TextAreaField
                  label="Catering"
                  icon={Coffee}
                  value={rider.catering}
                  onChange={(e) => setRider({ ...rider, catering: e.target.value })}
                  placeholder="e.g. Hot meal before performance, vegetarian options available, fruit and snacks..."
                />

                <TextAreaField
                  label="Beverages"
                  value={rider.beverages}
                  onChange={(e) => setRider({ ...rider, beverages: e.target.value })}
                  placeholder="e.g. Still water, energy drinks (Red Bull), soft drinks, no alcohol required..."
                />

                <TextAreaField
                  label="Transportation"
                  icon={Car}
                  value={rider.transportation}
                  onChange={(e) => setRider({ ...rider, transportation: e.target.value })}
                  placeholder="e.g. Airport pickup required, private car service to/from venue..."
                />

                <TextAreaField
                  label="Accommodation"
                  icon={Hotel}
                  value={rider.accommodation}
                  onChange={(e) => setRider({ ...rider, accommodation: e.target.value })}
                  placeholder="e.g. 4-star hotel minimum, single room, late checkout required..."
                />

                <TextAreaField
                  label="Guest List"
                  icon={Users}
                  value={rider.guest_list}
                  onChange={(e) => setRider({ ...rider, guest_list: e.target.value })}
                  placeholder="e.g. 4 guest list spots, 2 VIP/backstage passes..."
                />

                <TextAreaField
                  label="Other Hospitality Requests"
                  value={rider.other_hospitality}
                  onChange={(e) => setRider({ ...rider, other_hospitality: e.target.value })}
                  placeholder="Any other hospitality requirements or special requests..."
                />
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>

        <div className="mt-6">
          <Button
            onClick={handleSave}
            disabled={saving}
            className="w-full bg-violet-600 hover:bg-violet-500"
          >
            <Save className="w-4 h-4 mr-2" />
            {saving ? 'Saving...' : 'Save Rider'}
          </Button>
        </div>

        <p className="text-center text-white/40 text-sm mt-4">
          Your rider will be visible to clients when they view your profile or book you.
        </p>
      </div>
    </div>
  );
}
